<!--
  SecurityModesCard.vue
  보안 설정 「검사 모드」 카드(W6 A1 — 계약 README 「모드 스위치」, 설계 4-5). 「보안 설정」 화면 맨 위.

  - 키 8개(auditMode·permMode·accountActive·supportAccess·operatorActing·wsToken·companyScope·legacyAccess)의 지금 값·출처·마지막 변경.
    geoMode 는 「국외 허용 IP」 화면의 GeoModeCard 그대로.
  - [바꾸기]: 값 + 사유(필수) → 경고 확인창 → 저장(M! — axios 인터셉터가 본인 확인 창을 띄움).
    서버별 덮어쓰기는 접힌 「고급」 안. 이름·설명·값은 쉬운 말(10-31 — 운영자가 기술 점검표·코드 값을 몰라 지원 접속을 못 켬).
    켜기 전 기술 점검표는 inapi 문서에만 둔다(화면에서 저장을 막지 않음).
  - 카드 전체가 ops.security.manage 가 있을 때만(서버가 조회부터 @PermStrict — 지정 안 된 운영자는 열 때마다 403 이던 것, 10-30).
    introspect 를 받기 전엔 숨기고, perms 칸이 없으면(옛 inapi) 숨기지 않음 — 서버가 판정.
-->
<template>
  <div v-if="canManage" class="card content-wrap-card p-3 security-modes">
    <div class="head">
      <h3 class="fw-bold title">검사 모드</h3>
      <span class="pageDesc">배포 없이 바꿉니다(약 1분 안 모든 서버 반영). 환경마다 DB 가 따로라 각 환경에서 따로 바꿉니다.</span>
      <!-- 『막음』으로 바꾸기 전에 볼 곳(T-CONTRACT-1031 §3) -->
      <router-link to="/certilife/perm-observations" class="pageDesc">막혔을 요청 보기 →</router-link>
    </div>

    <div v-if="loadError" class="noResult">
      <div>{{ loadError }}</div>
      <a-button size="small" class="mt-2" @click="load">다시 불러오기</a-button>
    </div>
    <a-table
      v-else
      :columns="columns"
      :data-source="rows"
      :loading="loading"
      :pagination="false"
      :scroll="{ x: 860 }"
      row-key="key"
      size="small"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'name'">
          <!-- 스위치 코드 이름(auditMode 등)은 보이지 않는다(10-31 intest) -->
          <div class="fw-bold">{{ record.def.name }}</div>
          <div class="sub-text">{{ record.def.desc }}</div>
        </template>
        <template v-else-if="column.key === 'value'">
          <span :class="{ blocking: hasBlocking(record) }">{{ currentText(record) }}</span>
        </template>
        <template v-else-if="column.key === 'source'">{{ SOURCE_LABEL[record.source] || record.source }}</template>
        <template v-else-if="column.key === 'updated'">
          <template v-if="record.updatedAt">
            <div class="nowrap">{{ formatTime(record.updatedAt) }} {{ personText(record.updatedBy) }}</div>
            <div class="sub-text">"{{ record.reason || "-" }}"</div>
          </template>
          <template v-else>-</template>
        </template>
        <template v-else-if="column.key === 'actions'">
          <div class="row-actions">
            <a-button v-if="canManage" size="small" @click="openEdit(record)">바꾸기</a-button>
            <a-button size="small" type="link" @click="openHistory(record)">이력</a-button>
          </div>
        </template>
      </template>
    </a-table>

    <!-- 바꾸기 창 -->
    <a-modal :visible="!!editing" :confirm-loading="saving" :footer="null" width="640px" @cancel="closeEdit">
      <template #title>
        <div class="modal-title">{{ editing ? editing.def.name : "" }} 바꾸기</div>
      </template>
      <div v-if="editing" class="modal-content">
        <p class="sub-text">{{ editing.def.desc }}</p>
        <div class="sub-text mb-2">지금: {{ currentText(editing) }}</div>

        <template v-if="editing.def.legacy">
          <div v-for="k in LEGACY_KEYS" :key="k" class="legacy-row">
            <label class="field-label">{{ LEGACY_LABEL[k] }}</label>
            <a-radio-group v-model:value="draft.values[k]" button-style="solid" size="small">
              <a-radio-button v-for="v in editing.def.values" :key="v" :value="v">{{ valueLabel(v) }}</a-radio-button>
            </a-radio-group>
          </div>
        </template>
        <template v-else>
          <label class="field-label">바꿀 값</label>
          <a-radio-group v-model:value="draft.mode" button-style="solid" size="small">
            <a-radio-button v-for="v in editing.def.values" :key="v" :value="v">{{ valueLabel(v) }}</a-radio-button>
          </a-radio-group>

          <!-- 서버별 덮어쓰기는 접힌 「고급」 안(10-31) -->
          <div v-if="editing.def.perServer" class="advanced">
            <a-button size="small" type="link" class="advanced-toggle" @click="advanced = !advanced">
              고급 — 서버별 덮어쓰기{{ draft.perServer.length ? `(${draft.perServer.length}개)` : "" }} {{ advanced ? "접기" : "펼치기" }}
            </a-button>
            <template v-if="advanced">
              <p class="field-hint">이 서버만 다른 값을 씁니다. 보통은 비워 둡니다.</p>
              <div v-for="(p, i) in draft.perServer" :key="i" class="per-server-row">
                <a-select v-model:value="p.server" placeholder="서버·앱" style="width: 200px" size="small">
                  <a-select-option v-for="s in PER_SERVER_TARGETS" :key="s" :value="s">{{ s }}</a-select-option>
                </a-select>
                <a-select v-model:value="p.value" placeholder="값" style="width: 130px" size="small">
                  <a-select-option v-for="v in editing.def.values" :key="v" :value="v">{{ valueLabel(v) }}</a-select-option>
                </a-select>
                <a-button size="small" type="link" danger @click="draft.perServer.splice(i, 1)">빼기</a-button>
              </div>
              <a-button size="small" @click="draft.perServer.push({ server: undefined, value: undefined })">서버 더하기</a-button>
            </template>
          </div>
        </template>

        <div v-if="warnings" :class="['warning-box', { danger: warnings.danger }]">
          <div v-for="(line, i) in warnings.lines" :key="i">{{ line }}</div>
        </div>

        <label class="field-label">사유(필수)</label>
        <a-textarea v-model:value="draft.reason" :rows="2" :maxlength="200" placeholder="예: 11-19 메뉴 권한 막음 시작(시험 끝)" />

        <div class="modal-footer">
          <a-button @click="closeEdit">취소</a-button>
          <a-button type="primary" class="yellow-button" :loading="saving" :disabled="!!problem" @click="submit">저장</a-button>
        </div>
        <p v-if="problem" class="field-hint text-end">{{ problem }}</p>
        <p class="field-hint text-end">저장 전에 운영자 본인 확인을 합니다.</p>
      </div>
    </a-modal>

    <!-- 이력 창 -->
    <a-modal :visible="!!historyOf" :footer="null" width="760px" @cancel="historyOf = null">
      <template #title>
        <div class="modal-title">{{ historyOf ? historyOf.def.name : "" }} 바뀐 이력</div>
      </template>
      <div class="modal-content">
        <a-table
          :columns="historyColumns"
          :data-source="history"
          :loading="historyLoading"
          :pagination="{ pageSize: 10, hideOnSinglePage: true }"
          :scroll="{ x: 640 }"
          row-key="_rowKey"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <span v-if="column.key === 'at'" class="nowrap">{{ formatTime(record.at) }}</span>
          </template>
          <template #emptyText>
            <div class="noResult">{{ historyError || "바뀐 이력이 없습니다." }}</div>
          </template>
        </a-table>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { message } from "ant-design-vue";
import { confirmDialog } from "@/utils/utils";
import { errorMessage, formatTime, isStepUpCancelled } from "@/utils/mfa";
import { personText } from "@/utils/geoAllowIp";
import { canNow } from "@/utils/perm";
import {
  LEGACY_KEYS,
  LEGACY_LABEL,
  PER_SERVER_TARGETS,
  SOURCE_LABEL,
  buildPutBody,
  changeError,
  changeWarnings,
  currentText,
  draftOf,
  historyRow,
  isBlockingValue,
  normalizeSettings,
  valueLabel,
  withSavedMode,
} from "@/utils/securityModes";
import { standardErrorNotice } from "@/utils/standardErrors";
import { SecuritySettingsApi } from "@/api/standards";
import { notifyFailure } from "./GeoAllowIp/api";

export default {
  name: "SecurityModesCard",
  data() {
    return {
      LEGACY_KEYS,
      LEGACY_LABEL,
      PER_SERVER_TARGETS,
      SOURCE_LABEL,
      currentText,
      valueLabel,
      formatTime,
      personText,
      rows: normalizeSettings([]),
      loading: false,
      loadError: "",
      editing: null,
      draft: null,
      advanced: false,
      saving: false,
      historyOf: null,
      history: [],
      historyLoading: false,
      historyError: "",
      columns: [
        { key: "name", title: "이름", width: 170 },
        { key: "value", title: "지금 값" },
        { key: "source", title: "출처", width: 90 },
        { key: "updated", title: "마지막 변경", width: 220 },
        // 「바꾸기·이력」 버튼 — 1024 폭(표 안 가로 스크롤)에서도 보이게 오른쪽에 고정(FIX-1101 13)
        { key: "actions", title: "", width: 130, fixed: "right" },
      ],
      historyColumns: [
        { key: "at", dataIndex: "at", title: "시각", width: 140 },
        { key: "who", dataIndex: "who", title: "처리자", width: 120 },
        { key: "change", dataIndex: "change", title: "이전 → 이후" },
        { key: "reason", dataIndex: "reason", title: "사유", width: 180 },
      ],
    };
  },
  computed: {
    canManage() {
      return canNow(this.$store.state.introspect, "ops.security.manage");
    },
    problem() {
      return this.editing ? changeError(this.editing, this.draft) : "";
    },
    // 고르는 대로 무엇이 막히는지 미리 보인다(확인창에도 같은 문구)
    warnings() {
      return this.editing ? changeWarnings(this.editing, this.draft) : null;
    },
  },
  watch: {
    canManage: {
      immediate: true,
      handler(v) {
        if (v) this.load();
      },
    },
  },
  methods: {
    hasBlocking(row) {
      if (row.def.legacy) return LEGACY_KEYS.some((k) => row.values[k] === "block");
      return isBlockingValue(row.mode) || Object.values(row.perServer || {}).some(isBlockingValue);
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        this.rows = normalizeSettings(await SecuritySettingsApi.list());
      } catch (error) {
        console.error(error);
        this.loadError = isStepUpCancelled(error)
          ? "본인 확인을 취소해 검사 모드를 불러오지 못했습니다."
          : errorMessage(error, "검사 모드를 불러오지 못했습니다. 잠시 뒤 다시 시도하세요.");
      } finally {
        this.loading = false;
      }
    },
    openEdit(row) {
      this.editing = row;
      this.draft = draftOf(row);
      this.advanced = false;
    },
    closeEdit() {
      if (this.saving) return;
      this.editing = null;
      this.draft = null;
    },
    async submit() {
      const row = this.editing;
      const problem = changeError(row, this.draft);
      if (problem) {
        message.warning(problem);
        return;
      }
      const w = changeWarnings(row, this.draft);
      const ok = await confirmDialog(`「${row.def.name}」 값을 바꿀까요?`, w.lines.join(" "), {
        confirmText: "바꾸기",
        danger: w.danger,
      });
      if (!ok) return;
      this.saving = true;
      try {
        // 보낸 본문을 먼저 고정 — 응답을 기다리는 사이 창 값이 바뀌어도 서버에 저장된 값만 탭에 반영
        const body = buildPutBody(row, this.draft);
        await SecuritySettingsApi.put(row.key, body);
        this.$store.commit("setIntrospect", withSavedMode(this.$store.state.introspect, row.def, body.mode));
        message.success({ content: `「${row.def.name}」 값을 바꿨습니다. 약 1분 안에 모든 서버에 반영됩니다.`, duration: 6 });
        this.saving = false;
        this.closeEdit();
        this.load();
      } catch (error) {
        // 인터셉터가 이미 알린 새 계약 코드(AUDIT_UNAVAILABLE·PERMISSION_DENIED 등)는 다시 알리지 않는다
        if (standardErrorNotice(error)) console.error(error);
        else notifyFailure(error, "바꾸지 못했습니다. 잠시 뒤 다시 시도하세요.");
      } finally {
        this.saving = false;
      }
    },
    async openHistory(row) {
      this.historyOf = row;
      this.history = [];
      this.historyError = "";
      this.historyLoading = true;
      try {
        const list = await SecuritySettingsApi.history(row.key);
        this.history = list.map((r, i) => ({ ...historyRow(r), _rowKey: i }));
      } catch (error) {
        console.error(error);
        this.historyError = errorMessage(error, "이력을 불러오지 못했습니다.");
      } finally {
        this.historyLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.head {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}
.title {
  margin: 0;
  font-size: 1.4rem;
  color: #000;
}
.pageDesc {
  font-size: 0.9rem;
  color: #67748e;
}
.sub-text {
  font-size: 12px;
  color: #5c6168;
}
.nowrap {
  white-space: nowrap;
}
.blocking {
  color: #c62828;
  font-weight: 600;
}
.row-actions {
  display: flex;
  gap: 4px;
}
.field-label {
  display: block;
  margin: 14px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #2f3337;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #5c6168;
}
.legacy-row .field-label {
  margin-top: 8px;
}
/* 640px 창·좁은 창에서도 가로로 넘치지 않게: 긴 글은 줄바꿈, 줄은 접힘(10-31 intest 에서 점검표가 왼쪽이 잘림).
   여백은 레포 규칙 src/modal.mdc(전역 .modal-content 테두리·.ant-modal-content padding 0 을 덮음, FIX-1101 12) */
.modal-content {
  padding: 20px;
  border: none;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: keep-all;
}
.modal-content :deep(.ant-radio-group) {
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
}
.advanced {
  margin-top: 10px;
}
.advanced-toggle {
  padding-left: 0;
}
.per-server-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
}
.per-server-row :deep(.ant-select) {
  max-width: 100%;
}
.warning-box {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #f5f7f9;
  font-size: 13px;
  color: #2f3337;
}
.warning-box.danger {
  background: #fdecea;
  color: #b71c1c;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.security-modes :deep(.yellow-button),
.modal-footer :deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;
}
</style>
